// browserSupport.js
// Helper functions to check browser compatibility for various features

/**
 * Checks if the browser supports service workers
 * @returns {boolean} True if service workers are supported
 */
export const supportsServiceWorker = () => {
    return 'serviceWorker' in navigator;
  };
  
  /**
   * Checks if the browser supports notifications
   * @returns {boolean} True if notifications are supported
   */
  export const supportsNotifications = () => {
    return 'Notification' in window;
  };
  
  /**
   * Checks if the browser is Firefox
   * @returns {boolean} True if the browser is Firefox
   */
  export const isFirefox = () => {
    return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
  };
  
  /**
   * Checks if the browser is in private browsing mode
   * @returns {Promise<boolean>} Promise that resolves to true if private browsing is detected
   */
  export const isPrivateBrowsing = () => {
    return new Promise(resolve => {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      if (isSafari) {
        // Safari private browsing detection
        const storage = window.localStorage;
        try {
          storage.setItem('test', '1');
          storage.removeItem('test');
          resolve(false);
        } catch (e) {
          resolve(true);
        }
      } else if (navigator.userAgent.includes('Firefox')) {
        // Firefox private browsing detection
        const db = indexedDB.open('test');
        db.onerror = () => resolve(true);
        db.onsuccess = () => {
          db.result.close();
          indexedDB.deleteDatabase('test');
          resolve(false);
        };
      } else {
        const fs = window.RequestFileSystem || window.webkitRequestFileSystem;
        if (!fs) {
          resolve(false);
          return;
        }
        
        fs(window.TEMPORARY, 100, 
          () => resolve(false), 
          () => resolve(true)
        );
      }
    });
  };
  
  /**
   * Comprehensive check for Firebase messaging support
   * @returns {Promise<{supported: boolean, reason: string|null}>} Result object with support status and reason if not supported
   */
  export const checkFirebaseMessagingSupport = async () => {
    if (!supportsServiceWorker()) {
      return { 
        supported: false, 
        reason: 'Service Workers are not supported in this browser' 
      };
    }
    
    if (!supportsNotifications()) {
      return { 
        supported: false, 
        reason: 'Notifications are not supported in this browser' 
      };
    }
    
    // Check for private browsing mode
    const isPrivate = await isPrivateBrowsing();
    if (isPrivate && isFirefox()) {
      return { 
        supported: false, 
        reason: 'Firebase messaging is not supported in Firefox private browsing mode' 
      };
    }
    
    return { supported: true, reason: null };
  };
  